import React from 'react';
import { motion } from 'framer-motion';
import { User } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Gender } from '@/utils/ai-types';
import { cn } from '@/lib/utils';
import { InlineHelp } from '../WizardTooltip';

interface CharacterDetailsStepProps {
  name: string;
  age: string;
  gender: Gender | '';
  onNameChange: (name: string) => void;
  onAgeChange: (age: string) => void;
  onGenderChange: (gender: Gender) => void;
  errors?: {
    name?: string;
    age?: string;
    gender?: string;
  };
}

export function CharacterDetailsStep({
  name,
  age,
  gender,
  onNameChange,
  onAgeChange,
  onGenderChange,
  errors = {}
}: CharacterDetailsStepProps) {
  // Age options as pills
  const ageOptions = Array.from({ length: 13 }, (_, i) => i.toString());
  
  // Gender options with icons
  const genderOptions = [
    { value: Gender.MALE, label: 'Boy', icon: '👦' },
    { value: Gender.FEMALE, label: 'Girl', icon: '👧' },
    { value: Gender.OTHER, label: 'Other', icon: '🌟' }
  ];
  
  return (
    <div className="space-y-8">
      {/* Name input - the only text field */}
      <div className="space-y-2">
        <Label htmlFor="name" className="text-base font-medium flex items-center gap-2">
          Character's Name
          <InlineHelp content="Enter the name you want to appear in the story. This will be used throughout the book." />
        </Label>
        <Input
          id="name"
          type="text"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          onBlur={(e) => onNameChange(e.target.value.trim())}
          placeholder="Enter name"
          className={cn(
            "text-lg py-6",
            errors.name && "border-red-500 focus:ring-red-500"
          )}
          autoFocus
        />
        {errors.name && (
          <p className="text-sm text-red-500 mt-1">{errors.name}</p>
        )}
      </div>
      
      {/* Age pill selector */}
      <div className="space-y-3">
        <Label className="text-base font-medium flex items-center gap-2">
          Age
          <InlineHelp content="Select the character's age. This helps create age-appropriate story content." />
        </Label>
        <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
          {ageOptions.map((ageOption) => (
            <motion.button
              key={ageOption}
              type="button"
              onClick={() => onAgeChange(ageOption)}
              className={cn(
                "relative py-3 px-3 sm:px-4 rounded-full text-sm font-medium transition-all",
                "focus:outline-none focus:ring-2 focus:ring-offset-2",
                "min-h-[48px]", // Ensure touch target
                age === ageOption ? [
                  "bg-purple-600 text-white shadow-md",
                  "focus:ring-purple-500"
                ] : [
                  "bg-gray-100 text-gray-700 hover:bg-gray-200",
                  "focus:ring-gray-400"
                ]
              )}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {ageOption}
            </motion.button>
          ))}
          {/* 13+ option */}
          <motion.button
            type="button"
            onClick={() => onAgeChange('13')}
            className={cn(
              "relative py-3 px-3 sm:px-4 rounded-full text-sm font-medium transition-all",
              "focus:outline-none focus:ring-2 focus:ring-offset-2",
              "min-h-[48px]", // Ensure touch target
              parseInt(age) >= 13 ? [
                "bg-purple-600 text-white shadow-md",
                "focus:ring-purple-500"
              ] : [
                "bg-gray-100 text-gray-700 hover:bg-gray-200",
                "focus:ring-gray-400"
              ]
            )}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            13+
          </motion.button>
        </div>
        {errors.age && (
          <p className="text-sm text-red-500">{errors.age}</p>
        )}
      </div>
      
      {/* Gender icon selector */}
      <div className="space-y-3">
        <Label className="text-base font-medium">Gender</Label>
        <div className="grid grid-cols-3 gap-4">
          {genderOptions.map((option) => (
            <motion.button
              key={option.value}
              type="button"
              onClick={() => onGenderChange(option.value)}
              className={cn(
                "relative flex flex-col items-center justify-center p-6 rounded-xl transition-all",
                "border-2 focus:outline-none focus:ring-2 focus:ring-offset-2",
                gender === option.value ? [
                  "border-purple-600 bg-purple-50 shadow-md",
                  "focus:ring-purple-500"
                ] : [
                  "border-gray-200 bg-white hover:border-gray-300",
                  "focus:ring-gray-400"
                ]
              )}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <span className="text-4xl mb-2">{option.icon}</span>
              <span className={cn(
                "text-sm font-medium",
                gender === option.value ? "text-purple-900" : "text-gray-700"
              )}>
                {option.label}
              </span>
              
              {/* Selected indicator */}
              {gender === option.value && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute top-2 right-2 w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center"
                >
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </motion.div>
              )}
            </motion.button>
          ))}
        </div>
        {errors.gender && (
          <p className="text-sm text-red-500">{errors.gender}</p>
        )}
      </div>
    </div>
  );
}
